import Link from '@/routing/Link';
import { useRouter } from '@/routing/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useIdentityFieldListState } from "../required-fields/hooks/useIdentityFieldListState";
import {
  buildOptimisticValueMapField,
  revalidateIdentityValueMapWorkspace,
  syncIdentityWorkspaceCaches,
} from "../required-fields/lib/identity-patch-optimistic";
import {
  AlertCircle,
  ArrowLeft,
  Info,
  Loader2,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { SessionExpirySnackbar } from "../../components/SessionExpirySnackbar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/Text";
import { ImportActionBar } from "../../components/ImportActionBar";
import { DiscardImportSessionDialog } from "../../components/DiscardImportSessionDialog";
import { ImportStepHeader } from "../../components/ImportStepHeader";
import { useImportWizardContext } from "../../context/ImportWizardContext";
import { useIdentitySourceOptions } from "../../hooks/useIdentitySourceOptions";
import { useIdentityStep } from "../../hooks/useIdentityStep";
import { useIdentityValueMapWorkspace } from "../../hooks/useIdentityValueMapWorkspace";
import { getApiErrorMessage } from '@/lib/get-api-error-message'
import { IdentityWorkspaceInline } from "../required-fields/components/IdentityWorkspaceInline";
import { RequiredFieldsGrid } from "../required-fields/components/RequiredFieldsGrid";
import { useRequiredFieldsWorkspaceNavigation } from "../required-fields/hooks/useRequiredFieldsWorkspaceNavigation";
import { useWorkspaceDraftState } from "../required-fields/hooks/useWorkspaceDraftState";
import { useConfirmDialog } from "@/lib/hooks/useConfirmDialog";
import { DiscardWorkspaceEditsDialog } from "../required-fields/components/DiscardWorkspaceEditsDialog";
import {
  isIdentityFieldReady,
  resolveDisplayedWorkspaceMode,
  getDefaultWorkspaceMode,
  getWorkspaceModesFromField,
} from "../required-fields/lib/required-fields-ui";
import { buildAcceptInferredCustomValuesPayload } from "../required-fields/lib/inference-accept-payload";
import type {
  IdentityActiveWorkspace,
  IdentityFieldSummary,
  IdentitySourceOptions,
  IdentityValueMapWorkspace,
  IdentityWorkspaceMode,
} from "@/types/components/audiences/import-session";
import { useRealtimeRepair } from "@/lib/realtime/useRealtimeRepair";
import { incrementPollWonRepair } from "@/lib/realtime/pollWonMetrics";

const CANDIDATE_PAGE_SIZE = 50;

function toPreloadedSourceOptions(
  activeWorkspace: IdentityActiveWorkspace | null | undefined,
): IdentitySourceOptions | null {
  if (!activeWorkspace || activeWorkspace.mode !== "confirm_source") {
    return null;
  }
  return {
    canonicalId: activeWorkspace.canonicalId,
    currentSourceColumn:
      activeWorkspace.workspace.chooseColumnFix.currentSourceColumn ?? null,
    candidates: activeWorkspace.workspace.chooseColumnFix.candidates,
    candidateWindow: activeWorkspace.workspace.candidateWindow,
  };
}

function toPreloadedValueMapWorkspace(
  activeWorkspace: IdentityActiveWorkspace | null | undefined,
): IdentityValueMapWorkspace | null {
  const reviewValuesFix = activeWorkspace?.workspace.reviewValuesFix;
  if (
    !activeWorkspace ||
    activeWorkspace.mode !== "map_values" ||
    !reviewValuesFix
  ) {
    return null;
  }
  return {
    canonicalId: activeWorkspace.canonicalId,
    sourceColumn: reviewValuesFix.sourceColumn,
    currentValueMap: reviewValuesFix.currentValueMap ?? {},
    distinctValues: reviewValuesFix.distinctValues ?? [],
    sampleValues:
      reviewValuesFix.sampleValues ??
      activeWorkspace.workspace.field.sampleRawValues,
    previewRows: reviewValuesFix.previewRows,
    allColumnNames: reviewValuesFix.allColumnNames,
  };
}

export function RequiredFieldsStep() {
  const { t } = useTranslation("app");
  const {
    sessionId,
    snapshot,
    mutateSnapshot,
    goToStep,
    isSnapshotLoading,
  } = useImportWizardContext();
  const router = useRouter();
  const {
    shell,
    error,
    isLoading,
    lastPatchRequestId,
    isMutating,
    isPatching,
    isDiscarding,
    patchField,
    completeRequiredFieldsStep,
    discard,
  } = useIdentityStep(sessionId);
  const {
    requiredFields,
    fields,
    applyPatchResponse,
  } = useIdentityFieldListState({
    machineVersion: shell?.machineVersion,
    requiredFields: shell?.requiredFields,
  });
  // UI-only state — not part of workspace draft state
  const [actionError, setActionError] = useState<string | null>(null);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [isContinuing, setIsContinuing] = useState(false);
  const [pendingWorkspaceSelection, setPendingWorkspaceSelection] = useState<{
    canonicalId: string;
    mode: IdentityWorkspaceMode | null;
  } | null>(null);

  // Async confirm dialog — replaces the synchronous window.confirm().
  const { confirm, dialogOpen: confirmDialogOpen, onResolve } = useConfirmDialog();

  // Refs read by confirmLeaveWorkspace so it stays stable (no deps on draft state).
  // Updated every render after useWorkspaceDraftState (below) sets their values.
  const isDirtyRef = useRef(false);
  const mappingBusyRef = useRef(false);

  // Stable wrapper so onWorkspaceChange doesn't cause nav-hook re-creation
  // when draft state changes. onWorkspaceChangeRef is updated each render.
  const onWorkspaceChangeRef = useRef<() => void>(() => {});
  const stableOnWorkspaceChange = useCallback(() => {
    onWorkspaceChangeRef.current();
    setActionError(null);
    setPendingWorkspaceSelection(null);
  }, []);

  useEffect(() => {
    setActionError(null);
    setDiscardDialogOpen(false);
    setIsContinuing(false);
    setPendingWorkspaceSelection(null);
  }, [sessionId]);

  const backendActiveWorkspace = shell?.activeWorkspace ?? null;
  /** Server (or flow snapshot) says identity review is done — hide mode tabs, collapse workspace. */
  const identityWorkspaceChromeHidden =
    requiredFields?.readyToCreate === true ||
    snapshot?.branchSummaries?.identity?.state === "satisfied";
  /** Local grid shows every row satisfied OR server ready — enables "Set segmentation" (stale readyToCreate). */
  const canContinueToSegmentation =
    requiredFields?.readyToCreate === true ||
    (fields.length > 0 && fields.every((field) => field.satisfied));
  const identitySatisfiedOnServer =
    snapshot?.branchSummaries?.identity?.state === "satisfied";

  const awaitingIdentitySync =
    Boolean(sessionId) &&
    requiredFields?.readyToCreate === true &&
    !identitySatisfiedOnServer &&
    !isSnapshotLoading &&
    !isContinuing;

  useRealtimeRepair({
    active: awaitingIdentitySync,
    onRepair: (reason) => {
      incrementPollWonRepair('IMPORT', reason);
      void mutateSnapshot(undefined, { revalidate: true });
    },
  });

  useEffect(() => {
    if (!awaitingIdentitySync) {
      return;
    }
    void mutateSnapshot(undefined, { revalidate: true });
  }, [awaitingIdentitySync, mutateSnapshot]);

  // Async guard: if workspace is clean (isDirtyRef.current = false) or busy
  // loading (mappingBusyRef.current = true), resolve immediately without a
  // dialog. If dirty, open DiscardWorkspaceEditsDialog and await the choice.
  // Reads from refs so this callback stays stable and the nav hook's async
  // callbacks (focusField / focusMode / handleDialogOpenChange) don't recreate.
  const confirmLeaveWorkspace = useCallback(async (): Promise<boolean> => {
    if (mappingBusyRef.current) return false;
    if (!isDirtyRef.current) return true;
    return confirm();
  }, [confirm]);

  const {
    activeFieldId,
    workspaceMode,
    setWorkspaceMode,
    collapseField,
    focusField,
    focusMode,
    applyWorkspaceSelection,
    clearWorkspaceSelection,
    prefetchWorkspaceSelection,
  } = useRequiredFieldsWorkspaceNavigation({
    sessionId,
    fields,
    sourcePageSize: CANDIDATE_PAGE_SIZE,
    initialFieldId: backendActiveWorkspace?.canonicalId ?? null,
    initialWorkspaceMode: backendActiveWorkspace?.mode ?? null,
    confirmLeaveWorkspace,
    onWorkspaceChange: stableOnWorkspaceChange,
  });
  const activeField = useMemo(
    () =>
      activeFieldId == null
        ? null
        : fields.find((field) => field.canonicalId === activeFieldId) ?? null,
    [activeFieldId, fields],
  );
  const availableModes = activeField
    ? getWorkspaceModesFromField(activeField)
    : [];
  const fallbackMode =
    !activeField ? null : getDefaultWorkspaceMode(activeField);
  const rawCurrentMode =
    !activeField
      ? null
      : workspaceMode &&
          (availableModes.includes(workspaceMode) ||
            workspaceMode === 'map_values')
        ? workspaceMode
        : fallbackMode;
  const currentMode =
    activeField && rawCurrentMode
      ? resolveDisplayedWorkspaceMode(
          activeField,
          rawCurrentMode,
          workspaceMode,
        )
      : rawCurrentMode;

  const prevIdentityNextActionRef = useRef<{
    canonicalId: string | null;
    nextAction: IdentityFieldSummary["nextAction"] | null;
  }>({ canonicalId: null, nextAction: null });

  useEffect(() => {
    if (!activeField) {
      prevIdentityNextActionRef.current = {
        canonicalId: null,
        nextAction: null,
      };
      return;
    }
    const prev = prevIdentityNextActionRef.current;
    if (prev.canonicalId !== activeField.canonicalId) {
      prevIdentityNextActionRef.current = {
        canonicalId: activeField.canonicalId,
        nextAction: activeField.nextAction,
      };
      return;
    }
    if (
      prev.nextAction === "confirm_source" &&
      activeField.nextAction === "map_values" &&
      workspaceMode === "confirm_source"
    ) {
      setWorkspaceMode("map_values");
    }
    prevIdentityNextActionRef.current = {
      canonicalId: activeField.canonicalId,
      nextAction: activeField.nextAction,
    };
  }, [activeField, setWorkspaceMode, workspaceMode]);
  // Draft state hook — must be called BEFORE useIdentitySourceOptions so it
  // can supply debouncedSourceSearch to that hook.
  const draft = useWorkspaceDraftState({
    activeField,
    currentMode,
    fallbackMode,
    setWorkspaceMode,
    workspaceMode,
  });
  const {
    draftSourceColumn,
    draftConstantValue,
    mappingDirty,
    mappingBusy,
    sourceSearch,
    debouncedSourceSearch,
    sourceDropdownOpen,
    draftDistribution,
    isDirty: workspaceDirty,
    setDraftSourceColumn,
    setDraftConstantValue,
    setMappingDirty,
    setMappingBusy,
    setSourceSearch,
    setSourceDropdownOpen,
    setDraftDistribution,
    draftSourceColumnMatchesTrustedPick,
  } = draft;

  // Update refs on every render so confirmLeaveWorkspace always reads the
  // freshest values. activeFieldIdRef / workspaceModeRef are now removed —
  // the async dialog pattern eliminates the stale-closure risk they guarded against.
  isDirtyRef.current = workspaceDirty;
  mappingBusyRef.current = mappingBusy;
  onWorkspaceChangeRef.current = draft.reset;

  const preloadedSourceOptions = useMemo(
    () =>
      currentMode === "confirm_source" &&
      debouncedSourceSearch.length === 0 &&
      activeField &&
      backendActiveWorkspace?.canonicalId === activeField.canonicalId
        ? toPreloadedSourceOptions(backendActiveWorkspace)
        : null,
    [
      activeField,
      backendActiveWorkspace,
      currentMode,
      debouncedSourceSearch.length,
    ],
  );
  const preloadedValueMapWorkspace = useMemo(
    () =>
      currentMode === "map_values" &&
      activeField &&
      backendActiveWorkspace?.canonicalId === activeField.canonicalId
        ? toPreloadedValueMapWorkspace(backendActiveWorkspace)
        : null,
    [activeField, backendActiveWorkspace, currentMode],
  );
  const identityValueMapSourceKey = useMemo((): string | null => {
    if (!activeField) {
      return null;
    }
    const fromField = (activeField.currentSourceColumn ?? "").trim();
    if (fromField) {
      return fromField;
    }
    if (backendActiveWorkspace?.canonicalId === activeField.canonicalId) {
      const sc =
        backendActiveWorkspace.workspace?.reviewValuesFix?.sourceColumn;
      if (sc && sc.trim()) {
        return sc.trim();
      }
    }
    return null;
  }, [activeField, backendActiveWorkspace]);
  const workspaceOpen = activeField !== null;
  const sourceOptionsNeedServerData =
    workspaceOpen && (currentMode === "confirm_source" || currentMode === "map_values");
  const valueMapNeedsServerData = workspaceOpen && currentMode === "map_values";

  const {
    sourceOptions,
    error: sourceOptionsError,
    isValidating: isSourceOptionsValidating,
    isLoadingMore: isSourceOptionsLoadingMore,
    fetchMore: fetchMoreSourceOptions,
    resetAccumulation: resetSourceOptionsAccumulation,
  } = useIdentitySourceOptions(
    sessionId ?? "",
    activeField?.canonicalId ?? null,
    {
      enabled: Boolean(activeField && sourceOptionsNeedServerData),
      query:
        currentMode === "confirm_source" || currentMode === "map_values"
          ? debouncedSourceSearch
          : undefined,
      pageSize: CANDIDATE_PAGE_SIZE,
      lastMutationRequestId: lastPatchRequestId,
      subscribeToUpdates: false,
    },
  );
  const {
    valueMapWorkspace,
    error: valueMapError,
    isLoading: isValueMapLoading,
    isValidating: isValueMapValidating,
  } = useIdentityValueMapWorkspace(
    sessionId ?? "",
    activeField?.canonicalId ?? null,
    {
      enabled: Boolean(
        activeField && valueMapNeedsServerData && identityValueMapSourceKey,
      ),
      valueMapSourceKey: identityValueMapSourceKey,
      lastMutationRequestId: lastPatchRequestId,
      subscribeToUpdates: false,
    },
  );
  const fetchedSourceOptions =
    sourceOptions &&
    activeField &&
    sourceOptions.canonicalId === activeField.canonicalId
      ? sourceOptions
      : null;
  const safeSourceOptions =
    fetchedSourceOptions ?? preloadedSourceOptions;
  const fetchedValueMapWorkspace =
    valueMapWorkspace &&
    activeField &&
    valueMapWorkspace.canonicalId === activeField.canonicalId &&
    (identityValueMapSourceKey == null ||
      !identityValueMapSourceKey ||
      valueMapWorkspace.sourceColumn === identityValueMapSourceKey)
      ? valueMapWorkspace
      : null;
  const safeValueMapWorkspace =
    fetchedValueMapWorkspace ?? preloadedValueMapWorkspace;
  const allowSourceRemapWhileValueMapUnavailable =
    currentMode === "map_values" &&
    Boolean(safeSourceOptions) &&
    !safeValueMapWorkspace;
  const draftSourceColumnInCurrentCandidatePage = Boolean(
    safeSourceOptions?.candidates.some(
      (candidate) => candidate.sourceColumn === draftSourceColumn,
    ),
  );
  const canSubmitConfirmSource = Boolean(
    activeField &&
    (currentMode === "confirm_source" ||
      allowSourceRemapWhileValueMapUnavailable) &&
    draftSourceColumn &&
    safeSourceOptions &&
    (draftSourceColumnInCurrentCandidatePage ||
      draftSourceColumn === safeSourceOptions.currentSourceColumn ||
      draftSourceColumnMatchesTrustedPick) &&
    (activeField.requiresConfirmation ||
      activeField.nextAction === "confirm_source" ||
      activeField.satisfied === true ||
      draftSourceColumnMatchesTrustedPick ||
      activeField.state === "ready_from_source" ||
      draftSourceColumn !== (activeField.currentSourceColumn ?? "")),
  );
  const needsMapValuesSourceRemap =
    currentMode === "map_values" &&
    Boolean(safeValueMapWorkspace?.sourceColumn) &&
    Boolean(draftSourceColumn) &&
    draftSourceColumn !== (safeValueMapWorkspace?.sourceColumn ?? "");
  const canSubmitMapValuesSourceRemap = Boolean(
    activeField &&
    needsMapValuesSourceRemap &&
    safeSourceOptions &&
    draftSourceColumn &&
    (draftSourceColumnInCurrentCandidatePage ||
      draftSourceColumn === safeSourceOptions.currentSourceColumn ||
      draftSourceColumnMatchesTrustedPick) &&
    !isMutating,
  );
  const mapValuesConfirmSourceSubmit = useMemo(() => {
    if (currentMode !== "map_values") {
      return undefined;
    }
    const hideButton =
      Boolean(safeValueMapWorkspace) && !needsMapValuesSourceRemap;
    const canSubmit = allowSourceRemapWhileValueMapUnavailable
      ? canSubmitConfirmSource
      : canSubmitMapValuesSourceRemap;
      const submitLabel = needsMapValuesSourceRemap
      ? t("audienceCreate.useThisColumn")
      : t("audienceCreate.mapToQuestion");
    const submitHint = needsMapValuesSourceRemap
      ? t("audienceCreate.remapColumnHint")
      : null;
    return { hideButton, canSubmit, submitLabel, submitHint };
  }, [
    allowSourceRemapWhileValueMapUnavailable,
    canSubmitConfirmSource,
    canSubmitMapValuesSourceRemap,
    currentMode,
    needsMapValuesSourceRemap,
    safeValueMapWorkspace,
    t,
  ]);
  const activeWorkspaceError = valueMapNeedsServerData
    ? valueMapError
    : sourceOptionsNeedServerData
      ? sourceOptionsError
      : undefined;
  const isWorkspaceTransitioning =
    pendingWorkspaceSelection != null &&
    activeField?.canonicalId === pendingWorkspaceSelection.canonicalId &&
    currentMode === pendingWorkspaceSelection.mode;
  const isTransientWorkspaceConflict =
    isWorkspaceTransitioning &&
    ((activeWorkspaceError as { response?: { status?: number } } | undefined)?.response
      ?.status === 409);
  const visibleWorkspaceError = isTransientWorkspaceConflict
    ? undefined
    : activeWorkspaceError;
  // A new column-search key sets SWR isLoading even while keepPreviousData
  // is still showing the last candidates. Treating that as a first load
  // unmounts the picker and wipes the text the user is typing.
  const showWorkspaceLoader =
    isWorkspaceTransitioning ||
    (!visibleWorkspaceError &&
      ((sourceOptionsNeedServerData && !safeSourceOptions) ||
        (valueMapNeedsServerData &&
          !allowSourceRemapWhileValueMapUnavailable &&
          (isValueMapLoading || !safeValueMapWorkspace))));
  const showWorkspaceRefreshIndicator =
    (sourceOptionsNeedServerData &&
      Boolean(safeSourceOptions) &&
      isSourceOptionsValidating) ||
    (valueMapNeedsServerData &&
      Boolean(safeValueMapWorkspace) &&
      isValueMapValidating);
  const expiresAtMs = shell?.expiresAt
    ? new Date(shell.expiresAt).getTime()
    : null;
  const isExpiringSoon =
    expiresAtMs != null &&
    expiresAtMs > now &&
    expiresAtMs - now <= 15 * 60 * 1000;

  // Late-populate only from explicit backend state. If the shell seeded a stale
  // source before the dedicated source-options response arrived, replace it only
  // while the user has not made their own edit in this workspace.
  useEffect(() => {
    if (currentMode !== "confirm_source" || !activeField || !safeSourceOptions) {
      return;
    }
    const resolved =
      safeSourceOptions.currentSourceColumn ??
      safeSourceOptions.candidates.find((candidate) => candidate.selected)
        ?.sourceColumn;
    if (!resolved) {
      return;
    }
    const shouldSyncFromBackend =
      !draftSourceColumn ||
      (!workspaceDirty &&
        !draftSourceColumnMatchesTrustedPick &&
        draftSourceColumn !== resolved);
    if (!shouldSyncFromBackend) {
      return;
    }
    draft.setDraftSourceColumnAndBaseline(resolved);
  }, [
    activeField,
    currentMode,
    draft,
    draftSourceColumn,
    draftSourceColumnMatchesTrustedPick,
    safeSourceOptions,
    workspaceDirty,
  ]);

  const prevChromeHiddenRef = useRef(identityWorkspaceChromeHidden);
  useEffect(() => {
    const wasHidden = prevChromeHiddenRef.current;
    prevChromeHiddenRef.current = identityWorkspaceChromeHidden;
    if (!identityWorkspaceChromeHidden || activeFieldId == null) {
      return;
    }
    if (!wasHidden) {
      clearWorkspaceSelection();
    }
  }, [activeFieldId, identityWorkspaceChromeHidden, clearWorkspaceSelection]);

  useEffect(() => {
    if (!pendingWorkspaceSelection || !activeField) {
      return;
    }
    if (activeField.canonicalId !== pendingWorkspaceSelection.canonicalId) {
      return;
    }

    if (
      pendingWorkspaceSelection.mode === null ||
      pendingWorkspaceSelection.mode === "set_constant" ||
      pendingWorkspaceSelection.mode === "review_inference"
    ) {
      setPendingWorkspaceSelection(null);
      return;
    }

    if (
      pendingWorkspaceSelection.mode === "confirm_source" &&
      safeSourceOptions
    ) {
      setPendingWorkspaceSelection(null);
      return;
    }

    if (
      pendingWorkspaceSelection.mode === "map_values" &&
      safeValueMapWorkspace
    ) {
      setPendingWorkspaceSelection(null);
    }
  }, [
    activeField,
    pendingWorkspaceSelection,
    safeSourceOptions,
    safeValueMapWorkspace,
  ]);

  useEffect(() => {
    const intervalId = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (!workspaceDirty && !mappingBusy) {
      return;
    }
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [mappingBusy, workspaceDirty]);

  async function handlePatchSuccess(
    response: Awaited<ReturnType<typeof patchField>>,
    options?: {
      sourceColumn?: string;
      valueMap?: Record<string, string>;
      fromConfirmSource?: boolean;
      fromReSaveSatisfiedField?: boolean;
    },
  ) {
    const optimisticField = buildOptimisticValueMapField(
      activeField,
      response,
      options,
    );
    applyPatchResponse(response, optimisticField);
    setMappingBusy(false);
    setMappingDirty(false);
    if (options?.fromConfirmSource && sessionId && activeField?.canonicalId) {
      revalidateIdentityValueMapWorkspace(sessionId, activeField.canonicalId);
    }
    const shouldStayOnFieldAfterReSave =
      options?.fromReSaveSatisfiedField &&
      !(
        options?.fromConfirmSource &&
        activeField &&
        response.field.canonicalId === activeField.canonicalId &&
        (response.field.requiresValueMapping === true ||
          response.field.nextAction === "map_values")
      );
    if (shouldStayOnFieldAfterReSave) {
      syncIdentityWorkspaceCaches({
        sessionId,
        activeField,
        currentMode,
        debouncedSourceSearch,
        pageSize: CANDIDATE_PAGE_SIZE,
        options,
      });
      return;
    }

    const isIdentityResolved = response.requiredFields?.readyToCreate === true;
    if (isIdentityResolved) {
      setPendingWorkspaceSelection(null);
      clearWorkspaceSelection();
      syncIdentityWorkspaceCaches({
        sessionId,
        activeField,
        currentMode,
        debouncedSourceSearch,
        pageSize: CANDIDATE_PAGE_SIZE,
        options,
      });
      return;
    }

    // After confirm_source, open the value-map panel for the same field only
    // when that field still needs value mapping. Otherwise follow nextWorkspace
    // (e.g. advance to the next identity field the backend selected).
    if (
      options?.fromConfirmSource &&
      activeField &&
      response.field.canonicalId === activeField.canonicalId &&
      (response.field.requiresValueMapping === true ||
        response.field.nextAction === "map_values")
    ) {
      prefetchWorkspaceSelection(activeField.canonicalId, "map_values");
      applyWorkspaceSelection(activeField.canonicalId, "map_values");
      setPendingWorkspaceSelection({
        canonicalId: activeField.canonicalId,
        mode: "map_values",
      });
      syncIdentityWorkspaceCaches({
        sessionId,
        activeField,
        currentMode,
        debouncedSourceSearch,
        pageSize: CANDIDATE_PAGE_SIZE,
        options,
      });
      return;
    }

    const baseItems = response.requiredFields?.items ?? fields;
    const mergedFields = baseItems.map((field) =>
      field.canonicalId === response.field.canonicalId
        ? (optimisticField ?? response.field)
        : field,
    );
    const currentIndex = mergedFields.findIndex(
      (field) => field.canonicalId === response.field.canonicalId,
    );
    const nextSequentialField =
      currentIndex >= 0 ? (mergedFields[currentIndex + 1] ?? null) : null;
    if (nextSequentialField && !isIdentityFieldReady(nextSequentialField)) {
      const nextMode =
        nextSequentialField.requiresValueMapping ||
        nextSequentialField.nextAction === "map_values"
          ? "map_values"
          : getDefaultWorkspaceMode(nextSequentialField);
      prefetchWorkspaceSelection(nextSequentialField.canonicalId, nextMode);
      applyWorkspaceSelection(nextSequentialField.canonicalId, nextMode);
      setPendingWorkspaceSelection({
        canonicalId: nextSequentialField.canonicalId,
        mode: nextMode,
      });
    } else {
      setPendingWorkspaceSelection(null);
      clearWorkspaceSelection();
    }
    syncIdentityWorkspaceCaches({
      sessionId,
      activeField,
      currentMode,
      debouncedSourceSearch,
      pageSize: CANDIDATE_PAGE_SIZE,
      options,
    });
  }

  async function handleContinueToNextStep() {
    if (!sessionId || !canContinueToSegmentation || isMutating || isContinuing) {
      return;
    }

    setActionError(null);
    setIsContinuing(true);
    try {
      await completeRequiredFieldsStep();
      goToStep("segmentation");
    } catch (continueError) {
      setActionError(
        getApiErrorMessage(
          continueError,
          t("audienceCreate.failedContinueNextStep"),
        ),
      );
    } finally {
      setIsContinuing(false);
    }
  }

  async function handleConfirmSource() {
    const canSaveSource =
      canSubmitConfirmSource ||
      (currentMode === "map_values" && canSubmitMapValuesSourceRemap);
    if (!activeField || !canSaveSource || isMutating) {
      return;
    }
    if (currentMode === "map_values" && mappingDirty) {
      if (!(await confirm())) {
        return;
      }
    }
    setActionError(null);
    const wasAlreadyReady = isIdentityFieldReady(activeField);
    try {
      const response = await patchField(activeField.canonicalId, {
        action: "confirm_source",
        sourceColumn: draftSourceColumn,
      });
      await handlePatchSuccess(response, {
        sourceColumn: draftSourceColumn,
        fromConfirmSource: true,
        fromReSaveSatisfiedField: wasAlreadyReady,
      });
    } catch (patchError) {
      setActionError(
        getApiErrorMessage(patchError, t("audienceCreate.failedSaveSourceColumn")),
      );
    }
  }

  async function handleSetConstant() {
    if (!activeField || !draftConstantValue || isMutating) {
      return;
    }
    setActionError(null);
    const wasAlreadyReady = isIdentityFieldReady(activeField);
    try {
      const response = await patchField(activeField.canonicalId, {
        action: "set_constant",
        value: draftConstantValue,
      });
      await handlePatchSuccess(response, {
        fromReSaveSatisfiedField: wasAlreadyReady,
      });
    } catch (patchError) {
      setActionError(
        getApiErrorMessage(patchError, t("audienceCreate.failedSaveConstant")),
      );
    }
  }

  async function handleAcceptInferred() {
    if (!activeField || !activeField.allowInference || isMutating) {
      return;
    }
    setActionError(null);
    const wasAlreadyReady = isIdentityFieldReady(activeField);
    try {
      const response = await patchField(activeField.canonicalId, {
        action: "accept_inferred",
        ...buildAcceptInferredCustomValuesPayload(
          draftDistribution,
          activeField.suggestedInference?.values,
        ),
      });
      await handlePatchSuccess(response, {
        fromReSaveSatisfiedField: wasAlreadyReady,
      });
    } catch (patchError) {
      setActionError(
        getApiErrorMessage(
          patchError,
          t("audienceCreate.failedAcceptInference"),
        ),
      );
    }
  }

  async function handleApplyValueMap(
    sourceColumn: string,
    valueMap: Record<string, string>,
  ) {
    if (!activeField || isMutating) {
      return;
    }
    setActionError(null);
    try {
      const response = await patchField(activeField.canonicalId, {
        action: "save_value_map",
        sourceColumn,
        valueMap,
      });
      await handlePatchSuccess(response, { sourceColumn, valueMap });
    } catch (patchError) {
      setActionError(
        getApiErrorMessage(patchError, t("audienceCreate.failedSaveValueMapping")),
      );
    }
  }

  async function handleDiscard() {
    if (isMutating) {
      return;
    }
    setActionError(null);
    try {
      await discard();
      setDiscardDialogOpen(false);
      router.push("/audiences/new");
    } catch (discardError) {
      setActionError(
        getApiErrorMessage(
          discardError,
          t("audienceCreate.failedDiscardSession"),
        ),
      );
    }
  }

  if (isLoading && !shell) {
    return (
      <>
        <Skeleton className="h-8 w-48" />
        <Skeleton className="mx-auto h-7 w-64" />
        <Skeleton className="h-5 w-80" />
        <Skeleton className="h-[400px] rounded-2xl" />
      </>
    );
  }

  if (error || !shell) {
    return (
      <>
        <Button
          variant="ghost"
          size="sm"
          asChild
          className="-ml-3 self-start text-muted-foreground hover:text-foreground"
        >
          <Link href="/audiences/new">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("audienceCreate.startOver")}
          </Link>
        </Button>
        <ImportStepHeader
          title={t("audienceCreate.twinIdentityUnavailable")}
          helper={t("audienceCreate.sessionLoadFailed")}
        />
        <Alert variant="destructive" className="rounded-2xl">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error instanceof Error
              ? getApiErrorMessage(
                  error,
                  t("audienceCreate.sessionExpiredOrDiscarded"),
                )
              : t("audienceCreate.sessionExpiredOrDiscarded")}
          </AlertDescription>
        </Alert>
      </>
    );
  }

  if (snapshot?.preflightStatus === "pending") {
    return (
      <>
        <header className="space-y-3">
          <h1 className="font-display text-4xl font-normal leading-[1.2] tracking-[-0.02em] text-foreground dark:text-white">
            {t("audienceCreate.preparingDataset")}
          </h1>
          <Text
            size="lg"
            color="muted"
            className="leading-[1.6] tracking-[-0.02em]"
          >
            {t("audienceCreate.analyzingColumnsDemographics")}
          </Text>
          <Text
            size="lg"
            color="muted"
            className="leading-[1.6] tracking-[-0.02em] mt-2"
          >
            {t("audienceCreate.closeTabResumeLater")}
          </Text>
        </header>
        <div className="flex flex-1 flex-col items-center justify-center gap-4 py-20 motion-safe:animate-wizard-enter-content">
          <Loader2
            className="h-10 w-10 shrink-0 animate-spin text-primary"
            aria-hidden
          />
          <p className="text-center text-base text-muted-foreground dark:text-muted-foreground">
            {t("audienceCreate.hangTight")}
          </p>
        </div>
      </>
    );
  }

  if (
    snapshot?.preflightStatus === "rejected" ||
    snapshot?.preflightStatus === "failed"
  ) {
    const isRejection = snapshot.preflightStatus === "rejected";
    const error = snapshot.preflightError;
    return (
      <>
        <header className="space-y-3">
          <h1 className="font-display text-4xl font-normal leading-[1.2] tracking-[-0.02em] text-foreground dark:text-white">
            {isRejection
              ? t("audienceCreate.couldNotUseFile")
              : t("audienceCreate.prepareDatasetFailed")}
          </h1>
        </header>
        <Alert variant="destructive" className="rounded-2xl">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error?.message ||
              (isRejection
                ? t("audienceCreate.fileNoUsableRowsDemographics")
                : t("audienceCreate.analyzeDatasetUnexpected"))}
            {error?.userAction ? <> {error.userAction}</> : null}
          </AlertDescription>
        </Alert>
        <div className="flex flex-col items-start gap-3">
          <Text size="base" color="muted">
            {t("audienceCreate.startOverDifferentFile")}
          </Text>
          <Button asChild variant="default" size="lg">
            <Link href="/audiences/new">{t("audienceCreate.startOver")}</Link>
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <DiscardImportSessionDialog
        open={discardDialogOpen}
        onOpenChange={setDiscardDialogOpen}
        onConfirm={handleDiscard}
        isPending={isDiscarding}
      />
      <DiscardWorkspaceEditsDialog
        open={confirmDialogOpen}
        onKeepEditing={() => onResolve(false)}
        onDiscard={() => onResolve(true)}
      />
      <header className="space-y-3">
        <h1 className="font-display text-4xl font-normal leading-[1.2] tracking-[-0.02em] text-foreground dark:text-white">
          {t("audienceCreate.defineIdentityFields")}
        </h1>
        <Text size="lg" color="muted" className="leading-[1.6] tracking-[-0.02em]">
          {t("audienceCreate.defineIdentityHelper")}
        </Text>
        <div className="flex items-start gap-3 rounded-lg bg-muted/30 dark:bg-muted px-3 py-2">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary dark:text-muted-foreground" />
          <p className="text-base leading-relaxed text-foreground/80 dark:text-muted-foreground">
            {t("audienceCreate.identityNoDataHint")}
          </p>
        </div>
      </header>
      <div className="flex flex-1 flex-col gap-6 motion-safe:animate-wizard-enter-content">
        {actionError && !activeField ? (
          <Alert variant="destructive" className="rounded-2xl">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{actionError}</AlertDescription>
          </Alert>
        ) : null}
        {isExpiringSoon ? (
          <SessionExpirySnackbar message={t("audienceCreate.identityWorkspaceExpiry")} />
        ) : null}
        {visibleWorkspaceError ? (
          <Alert variant="destructive" className="rounded-2xl">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {getApiErrorMessage(
                visibleWorkspaceError,
                t("audienceCreate.workspaceLoadFailed"),
              )}
            </AlertDescription>
          </Alert>
        ) : null}
        <RequiredFieldsGrid
          fields={fields}
          activeFieldId={activeFieldId}
          disabled={isPatching || mappingBusy || isWorkspaceTransitioning}
          onCollapseField={collapseField}
          onSelectField={(fieldId) => focusField(fieldId)}
          expandedContent={
            activeField ? (
              <IdentityWorkspaceInline
                nested
                activeField={activeField}
                availableModes={availableModes}
                currentMode={currentMode}
                allFieldsSatisfied={identityWorkspaceChromeHidden && activeFieldId == null}
                actionError={actionError}
                showWorkspaceRefreshIndicator={showWorkspaceRefreshIndicator}
                showWorkspaceLoader={showWorkspaceLoader}
                isPatching={isPatching}
                isLoadingMore={isSourceOptionsLoadingMore}
                mappingBusy={mappingBusy}
                sourceOptions={safeSourceOptions}
                valueMapWorkspace={safeValueMapWorkspace}
                draftSourceColumn={draftSourceColumn}
                sourceSearch={sourceSearch}
                sourceDropdownOpen={sourceDropdownOpen}
                draftConstantValue={draftConstantValue}
                workspaceDirty={workspaceDirty}
                draftDistribution={draftDistribution}
                canSubmitConfirmSource={canSubmitConfirmSource}
                mapValuesConfirmSourceSubmit={mapValuesConfirmSourceSubmit}
                onFocusMode={focusMode}
                onDraftSourceColumnChange={setDraftSourceColumn}
                onSourceSearchChange={setSourceSearch}
                onLoadMore={fetchMoreSourceOptions}
                onDropdownClose={resetSourceOptionsAccumulation}
                onSourceDropdownOpenChange={setSourceDropdownOpen}
                onDraftConstantValueChange={setDraftConstantValue}
                onDraftDistributionChange={setDraftDistribution}
                onMappingDirtyChange={setMappingDirty}
                onMappingBusyChange={setMappingBusy}
                onConfirmSource={() => void handleConfirmSource()}
                onSetConstant={() => void handleSetConstant()}
                onAcceptInferred={() => void handleAcceptInferred()}
                onApplyValueMap={handleApplyValueMap}
              />
            ) : undefined
          }
        />
        <ImportActionBar
          backLabel={t("audienceCreate.redefineContext")}
          backVariant="secondary"
          onBack={() => goToStep("context")}
          backDisabled={isMutating || isContinuing}
          nextLabel={t("audienceCreate.steps.segmentation")}
          onNext={() => void handleContinueToNextStep()}
          nextDisabled={
            !canContinueToSegmentation ||
            isMutating ||
            isContinuing
          }
          nextLoading={
            isContinuing ||
            (requiredFields?.readyToCreate === true &&
              !identitySatisfiedOnServer)
          }
          nextLoadingLabel={
            isContinuing ? t("audienceCreate.continuing") : t("audienceCreate.confirming")
          }
        />
      </div>
    </>
  );
}
